import { distributeCentsLargestRemainder } from './allocation.js';
/** Strips stale SP5 §1 policy state (`adjustment`/`policyFlag`) from a copy
 *  of `base` — pass-through and flag outcomes must never leak a PRIOR
 *  resolve's fields (rule 1/8: stale policy state never survives a
 *  recompute). Deliberately does NOT touch `ruleFlags` — that's SP5 §2.3's
 *  transaction-rule outcome, a separate subsystem this function never sets. */
function stripPolicyFields(base) {
    const { adjustment, policyFlag, ...rest } = base;
    return rest;
}
/** Resolves `cfg.deltaResolution` in order: rules (first `evalWhen` truthy
 *  wins; `null`/`false` = no-match) → top-level `calculation` → top-level
 *  `defaultTargetId`. A `calc` match on the `overPay` side ABORTS the chain
 *  (→ flag fallback) — it does NOT fall through to later entries or
 *  `defaultTargetId` (runtime guard — overPay never scales
 *  coupons/allocations up; the publish-time validator already blocks
 *  configuring it this way).
 *  Returns `undefined` when nothing in the chain matches (caller applies the
 *  shortPay implicit default / flag fallback). */
function resolveChain(cfg, side, evalWhen) {
    const usable = (r) => (r.kind === 'calc' && side !== 'shortPay' ? undefined : r);
    const rules = cfg.deltaResolution?.rules ?? [];
    for (let i = 0; i < rules.length; i++) {
        const rule = rules[i];
        if (!evalWhen(rule.when))
            continue;
        const ref = `rule:${i}`;
        if (rule.targetId !== undefined)
            return usable({ kind: 'target', targetId: rule.targetId, resolution: 'rule', ref });
        if (rule.calculation !== undefined)
            return usable({ kind: 'calc', calc: rule.calculation, resolution: 'rule', ref });
    }
    if (cfg.deltaResolution?.calculation !== undefined) {
        const calc = cfg.deltaResolution.calculation;
        return usable({ kind: 'calc', calc, resolution: 'calculation', ref: `calc:${calc}` });
    }
    if (cfg.deltaResolution?.defaultTargetId !== undefined) {
        const targetId = cfg.deltaResolution.defaultTargetId;
        return { kind: 'target', targetId, resolution: 'defaultTarget', ref: `target:${targetId}` };
    }
    return undefined;
}
/**
 * SP5 §1 — resolves an in-bound `imbalanced` `BalanceSnapshot` against the
 * job's `balancePolicy`: pass-through (no policy / not imbalanced / side
 * absent / mode block / out of bound), flag (mode flag, or accept whose
 * chain resolves to nothing), or accept (rules → calculation →
 * defaultTargetId → shortPay-only implicit `largest-remainder` default).
 * Pure: no `Date.now` (copies `computedAt` from `base`), no input mutation.
 * `allocations` rows with `source:'policy'` are ignored when deriving —
 * `policyRows` is the complete replacement set, not an accumulation.
 */
export function applyBalancePolicy(input) {
    const passThrough = () => ({
        snapshot: stripPolicyFields(input.base),
        policyRows: [],
        couponAdjustments: [],
    });
    if (!input.policy || input.base.verdict !== 'imbalanced')
        return passThrough();
    const side = input.base.deltaCents < 0 ? 'shortPay' : 'overPay';
    const cfg = input.policy[side];
    if (!cfg || cfg.mode === 'block')
        return passThrough();
    const mag = Math.abs(input.base.deltaCents);
    if (cfg.boundCents !== undefined && mag > cfg.boundCents)
        return passThrough();
    const flag = () => ({
        snapshot: { ...stripPolicyFields(input.base), policyFlag: side },
        policyRows: [],
        couponAdjustments: [],
    });
    if (cfg.mode === 'flag')
        return flag();
    // mode === 'accept' — resolve the chain; shortPay falls back to the
    // implicit largest-remainder default when nothing resolves, overPay flags.
    const chain = resolveChain(cfg, side, input.evalWhen);
    if (!chain && side !== 'shortPay')
        return flag();
    const resolved = chain ?? { kind: 'calc', calc: 'largest-remainder', resolution: 'calculation', ref: 'calc:largest-remainder' };
    const withAdjustment = () => ({
        ...stripPolicyFields(input.base),
        verdict: 'balanced',
        adjustment: {
            side, deltaCents: input.base.deltaCents, resolution: resolved.resolution, ref: resolved.ref,
            via: resolved.kind === 'target' ? 'target' : resolved.calc,
        },
    });
    if (resolved.kind === 'target') {
        return {
            snapshot: withAdjustment(),
            policyRows: [{ targetId: resolved.targetId, amountCents: input.base.deltaCents, source: 'policy' }],
            couponAdjustments: [],
        };
    }
    // resolved.kind === 'calc' — resolveChain already guarantees shortPay here
    // (an overPay calc match is normalized to "no match" and handled above).
    if (resolved.calc === 'largest-remainder') {
        const eligible = input.coupons.filter((c) => c.appliedCents !== null);
        if (eligible.length === 0)
            return flag();
        const weights = eligible.map((c) => ({ id: c.documentId, weight: c.appliedCents }));
        const dist = distributeCentsLargestRemainder(mag, weights);
        const reductionById = new Map(dist.map((d) => [d.id, d.amountCents]));
        return {
            snapshot: withAdjustment(),
            policyRows: [],
            couponAdjustments: eligible.map((c) => ({
                documentId: c.documentId,
                appliedCents: c.appliedCents - (reductionById.get(c.documentId) ?? 0),
            })),
        };
    }
    // resolved.calc === 'pro-rata-allocations'
    const nonPolicyRows = input.allocations.filter((r) => r.source !== 'policy');
    if (nonPolicyRows.length === 0)
        return flag();
    const weights = nonPolicyRows.map((r) => ({ id: r.targetId, weight: r.amountCents }));
    const dist = distributeCentsLargestRemainder(mag, weights);
    return {
        snapshot: withAdjustment(),
        policyRows: dist.map((d) => ({ targetId: d.id, amountCents: -d.amountCents, source: 'policy' })),
        couponAdjustments: [],
    };
}
//# sourceMappingURL=balance-policy.js.map